import { useInfiniteHits } from 'react-instantsearch';
import { useTranslation } from '@klv/hooks/useTranslation';

import Button from '@klv/atoms/button';
import useNormalizeSeachHits from '@klv/organisms/unified-search/hooks/use-normalize-search-hits';
import { generateClassName } from '@klv/shared/utils/functions';

import UnifiedSearchResultGridItem from '../unified-search-result-grid-item';
import UnifiedSearchResultListItem from '../unified-search-result-list-item';

import { processHref } from './utils';

import * as s from './styles/unified-search-results.module.css';

import type { IUnifiedSearchResults, IUnifiedSearchResult } from './types';

const UnifiedSearchResults = ({
  backgroundColor = 'light',
  className = '',
  defaultIndex,
  index,
  selectedIndex,
  setSelectedIndex,
  title,
  type,
  userCourseData,
  viewedResources,
  ...props
}: IUnifiedSearchResults) => {

  const { t } = useTranslation();
  const normalizeHit = useNormalizeSeachHits(index);
  const { hits, showMore, isLastPage, sendEvent } = useInfiniteHits(props);

  // if there are no hits, return null
  if (!hits.length) return null;

  const results = hits.map((hit) => {
    // @ts-ignore - Our various algolia hits are all different shapes, so we need to handle them differently
    const normalizedHit = normalizeHit(hit);
    return {
      ...normalizedHit,
      sendEvent: () => sendEvent('click', hit, 'Hit Clicked'),
      userCourseData: userCourseData?.data,
      viewedResources,
    };
  });

  const titleMap = (titleText: string) => {
    if (titleText === 'Help center')
      return 'Help article';
    else if (titleText === 'Community')
      return 'Community Post';
    else if (titleText === 'Developers')
      return 'Developer guide';
    return titleText;
  };

  const Result = type === 'grid' ? UnifiedSearchResultGridItem : UnifiedSearchResultListItem;
  const buttonColor = backgroundColor === 'dark' ? 'light' : 'dark';
  const onAcademy =
    defaultIndex === 'academy_courses'
    && selectedIndex === 'academy_courses';

  return (
    <div data-testid='unified-search-results' className={generateClassName([className, s.searchResults, 'searchResults', s[backgroundColor]])} {...props}>
      {title && (<div className={generateClassName([s.title, 'p1', 'semibold'])}>{title}</div>)}
      <div className={generateClassName([s[type], 'resultsHits'])}>
        {results.map((hit) => {
          if (!('pill' in hit)) {
            // @ts-ignore - Our various algolia hits are all different shapes, so we need to handle them differently
            hit.pill = titleMap(title);
          }
          return (
            <Result
              key={JSON.stringify(hit)}
              parentBackgroundColor={backgroundColor}
              {...hit as IUnifiedSearchResult}
              // @ts-ignore - Our various algolia hits are all different shapes, so we need to handle them differently
              link={processHref({ href: hit.link, makeRelative: onAcademy && hit?.pill !== 'Live training' })} // make relative if it's an academy course and NOT a live training
            />
          );
        })}
      </div>
      {(selectedIndex === '' && setSelectedIndex) && <Button buttonColor={buttonColor} className={s.viewAllButton} text={t('pages_search_viewAllResults', { index: title })} action={() => setSelectedIndex(index)} variant='tertiary' />}
      {!isLastPage && !!selectedIndex && <Button buttonColor={buttonColor} className={s.viewMoreButton} text={t('pages_search_viewMoreResults')} action={showMore} variant='tertiary' />}
    </div>
  );
};

export default UnifiedSearchResults;
